<template>
  <div class="create-work-form">
    <div class="content">
      <div class="form-group flex-grow p-8">
        <div class="header">
          <h2>Embed video</h2>
          <button class="close-btn" @click="close()">×</button>
        </div>

        <!-- Video URL input -->
        <label for="video">Video Url</label>
        <input
            type="text"
            name="video"
            class="mb-2"
            placeholder="Video Url"
            v-model="videoUrl"
        />

        <!-- Start and End time inputs -->
        <label>Adjust time frame</label>
        <div class="time flex justify-between items-center">
          <div class="start">
            <div class="item-title">
              <span>Start:</span>
              <input
                  type="text"
                  name="start"
                  :value="formatTime(sliderValue[0])"
                  disabled
                  :min="0"
                  :max="duration"
                  @input="restartAutoplay"
              />
            </div>
          </div>
          <div class="end">
            <div class="item-title">
              <span>End:</span>
              <input
                  type="text"
                  name="end"
                  disabled
                  :value="formatTime(sliderValue[1])"
                  :min="0"
                  :max="duration"
                  @input="restartAutoplay"
              />
            </div>
          </div>
        </div>

        <!-- Total Duration Display -->
        <label class="mt-3 total-duration" v-if="duration > 0">
          Total duration: {{ formatTime(sliderValue[1] - sliderValue[0]) }}
        </label>

        <!-- Slider for adjusting start and end -->
        <client-only>
          <VueSlider
              v-model="sliderValue"
              :min="0"
              :max="duration"
              :interval="1"
              :tooltip="'none'"
              :lazy="true"
              :useKeyboard="true"
              :enableCross="false"
              :formatter="formatTime"
              :dotSize="12"
          />
        </client-only>


        <small><u>Click here</u> to learn about the upload qualities.</small>


        <div class="mt-4" v-if="false">

          <div class="flex justify-start items-center">
            <span style="min-width: 100px">Muted</span>
            <input type="checkbox" v-model="muted"/>
          </div>
          <div class="flex justify-start items-center">
            <span style="min-width: 100px">Loop</span>
            <input  type="checkbox" v-model="loop"/>
          </div>
        </div>
      </div>

      <div class="form-group player flex-grow">
        <div class="player-container" ref="playerContainer" :style="videoData ? playerStyle : ''">
          <div ref="vimeoPlayer" class="vimeo-player"></div>
        </div>
      </div>
    </div>

    <div class="footer">
      <button class="create-btn" :disabled="!canCreate" @click="submitVideo">
        Save
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';

import * as pkg from 'vue-toastification';
const { useToast } = pkg;
import axios from 'axios';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css'; // Import the default theme for vue-slider-component
import Player from '@vimeo/player'; // Import the Vimeo Player

const toast = useToast();
const props = defineProps({
  videoUrl: {
    type: String,
    default: '',
  },
  editing: {
    type: Boolean,
    default: false,
  },
});

// Helper function to extract query parameters from URL
const getQueryParams = (url: string) => {
  const urlParams = new URLSearchParams(new URL(url).search);
  const start = urlParams.get('start') || '0';
  const end = urlParams.get('end');
  return {
    start: parseFloat(start),
    end: end ? parseFloat(end) : null,
  };
};
const emit = defineEmits(['close']);
const videoUrl = ref(props.videoUrl);
const videoData = ref<any>(null);
const duration = ref(0); // Duration in seconds
const sliderValue = ref([0, 0]); // The range for the start and end points
const canCreate = ref(false);
const player = ref<any>(null);
const playerReady = ref(false); // To track when the player is ready
const vimeoPlayer = ref<HTMLDivElement | null>(null); // To reference the player element
const playerContainer = ref<HTMLDivElement | any>(null);

const muted = ref(true); // State for muted checkbox
const loop = ref(true); // State for loop checkbox
// Watch for changes in sliderValue and update start and end
watch(sliderValue, (newValue) => {
  restartAutoplay();
}, { deep: true });

// Watch for video URL changes and fetch video data
watch(videoUrl, () => {
  if (videoUrl.value !== '' && videoUrl.value.length > 5) {
    fetchVideo();
  } else {
    canCreate.value = false;
  }
});
const aspectRatio = computed(() => {
  if (videoData.value) {
    return videoData.value.height / videoData.value.width;
  }
  return 9 / 16; // Default to 16:9 if videoData is not available
});

const playerStyle = computed(() => {

  console.log('player cont', playerContainer.value, aspectRatio.value);
  if (playerContainer.value) {
    const width = playerContainer.value.clientWidth; // Get the parent container width
    const height = width * aspectRatio.value; // Calculate the height using the aspect ratio
    console.log('player cont', playerContainer.value, aspectRatio.value, width, height);
    return {
      width: `${width}px`,
      height: `${height}px`,
    };
  } else {
    return {
      width: '0px',
      height: '0px',
    };
  }
});

// Initialize Vimeo player when the component is mounted
onMounted(async () => {
  if (videoUrl.value !== '') {
    const { start, end } = getQueryParams(videoUrl.value);
    console.log('vid url', videoUrl.value, props.videoUrl, start, end);
    await fetchVideo();
    sliderValue.value = [start, end || duration.value]; // Set the slider based on query params
  }
  await nextTick(); // Ensure DOM is fully loaded
});

// Cleanup when component is unmounted
onUnmounted(() => {
  if (player.value) {
    player.value.destroy();
  }
});

const close = () => {
  emit('close', null);
};

const getVimeoVideoId = (url: string) => {
  const regex = /vimeo\.com\/(?:.*#|.*\/videos\/)?([0-9]+)(?:$|\/|\?)/;
  const match = regex.exec(url);
  return match ? match[1] : null;
};

const fetchVideo = async () => {
  try {
    const videoId = getVimeoVideoId(videoUrl.value);
    videoData.value = null;
    duration.value = 0;
    sliderValue.value = [0, 0];
    canCreate.value = false;
    playerInstance = null;
    console.log('vid id', videoId);
    if (!videoId) {
      toast.error('Invalid Vimeo URL');
      return;
    }

    const response = await axios.get(`https://api.vimeo.com/videos/${videoId}`, {
      headers: {
        Authorization: `Bearer 2882f8d876e951dfb0357c88629dcf78`, // Replace with your Vimeo access token
      },
    });

    videoData.value = response.data;
    duration.value = videoData.value.duration; // Get video duration
    sliderValue.value = [sliderValue.value[0], sliderValue.value[1] || videoData.value.duration]; // Adjust end if needed
    canCreate.value = true;
    initVimeoPlayer(videoId);
  } catch (error) {
    toast.error('Failed to fetch video details');
    canCreate.value = false;
  }
};

watch([muted, loop], () => {
  updatePlayerSettings();
});

const updatePlayerSettings = () => {
  if (!playerInstance && vimeoPlayer.value) {
    playerInstance = new Player(vimeoPlayer.value);
  }
  if (playerInstance) {
    // Always set muted to true for video settings
    playerInstance.setMuted(true);
    playerInstance.setLoop(loop.value);
  }
};
let playerInstance = null as any; // Use a simple variable for storing the player instance

const restartAutoplay = () => {
  // Check if the playerInstance already exists to avoid re-initialization
  if (!playerInstance && vimeoPlayer.value) {
    const videoId = getVimeoVideoId(videoUrl.value);
    initVimeoPlayer(videoId);
  }

  if (playerInstance) {
    playerInstance.ready().then(async () => {
      console.log('restart autoplay');
      
      // Ensure player is muted before any playback
      await playerInstance.setMuted(true);
      
      // Remove any existing timeupdate listeners to avoid conflicts
      try {
        playerInstance.off('timeupdate');
      } catch (e) {
        // ignore if no listeners exist
      }
      
      await playerInstance.setCurrentTime(sliderValue.value[0]);
      await playerInstance.play();
      console.log('played', playerInstance);

      playerInstance.on('timeupdate', (data) => {
        if (data.seconds >= sliderValue.value[1]) {
          if (loop.value) {
            playerInstance.setCurrentTime(sliderValue.value[0]).then(() => {
              // Ensure it stays muted during loop
              playerInstance.setMuted(true);
            });
          } else {
            playerInstance.pause();
          }
        }
      });
    }).catch((error) => {
      console.error('Player is not ready:', error);
    });
  }
};

const initVimeoPlayer = (videoId: string) => {
  if (!playerInstance && vimeoPlayer.value) {
    const width = playerContainer.value.clientWidth;
    const height = width * aspectRatio.value;

    playerInstance = new Player(vimeoPlayer.value, {
      id: parseInt(videoId),
      autoplay: true,
      loop: loop.value,
      muted: true, // Always start muted
      width,
      height,
      controls: false,
    }) as any;

    playerInstance.ready().then(async () => {
      console.log('player ready');
      
      // Ensure player is muted immediately
      await playerInstance.setMuted(true);
      
      await playerInstance.setCurrentTime(sliderValue.value[0]);
      await playerInstance.play();

      playerInstance.on('timeupdate', (data) => {
        if (data.seconds >= sliderValue.value[1]) {
          if (loop.value) {
            playerInstance.setCurrentTime(sliderValue.value[0]).then(() => {
              // Ensure it stays muted during loop
              playerInstance.setMuted(true);
            });
          } else {
            playerInstance.pause();
          }
        }
      });
      
      // Extra safety: ensure muted on play events
      playerInstance.on('play', async () => {
        await playerInstance.setMuted(true);
      });
    }).catch((error) => {
      console.error('Player is not ready:', error);
    });
  }
};

// Format time for display
const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Submit video data
const submitVideo = () => {
  const [start, end] = sliderValue.value;
  const vidData = videoData.value;
  vidData.link = `${videoData.value.link}?start=${start}&end=${end}`
  emit('close', `${vidData.link}`, videoData.value);
};
</script>


<style scoped>
.form-group .player {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: black;
}
.player-container {
}
.time > div {
  max-width: 45%;
  flex: 1;
}

.time .start,
.time .end {
  display: flex;
  border: 1px solid #b8b8b8;
  border-radius: 5px;
}

.time .item-title {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 0 5px;
}

.time .item-title input {
  border: unset;
  font-size: 10px;
  padding: 5px;
}
.time .item-title {
  padding: 5px;
  flex: 1;
  text-align: center;
}

.create-work-form {
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.header h2 {
  font-size: 28px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
}

.content {
  display: flex;
  padding: 0;
  justify-content: center;
  align-items: center;
  background: black;
}

.form-group {
  max-width: 50%;
}

.form-group:first-child {
  background: white;
}
.form-group:last-child {
  background: black;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 14px;
}

.form-group small {
  display: block;
  margin-top: 10px;
  color: #b8b8b8;
  font-size: 8px;
  cursor: pointer;
}

.content-images {
  max-width: 100%;
  overflow: auto;
}

.content-images label {
  font-size: 14px;
  margin-bottom: 10px;
  display: block;
}

.content-image-horizontal {
  padding: 20px;
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  border-radius: 10px;
  position: relative; /* Ensure it is relative for the absolute positioned popover */
}

.content-image-horizontal img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 10px;
}

.footer {
  display: flex;
  justify-content: center;
  background: #EDEDED;
  padding: 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.add-btn {
  background-color: white;
  border: 1px solid #b8b8b8;
  padding: 0px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 12px;
  color: black;
}

.create-btn {
  background-color: #ccc;
  border: none;
  min-width: 140px;
  padding: 5px 0;
  border-radius: 25px;
  cursor: not-allowed;
  color: #b8b8b8;
}

.create-btn:enabled {
  background-color: #E3FF00;
  cursor: pointer;
  color: black;
}

.add {
  flex: 0;
  width: 100px;
  min-width: 100px;
  height: 100px;
  border: 1px solid #b8b8b8;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

.add img {
  width: 40px;
  height: 40px;
}

.form-group .img {
  width: 100%;
  height: 100%;
  background: black;
}

.time .item-title {
  color: #b8b8b8;
  font-size: 10px;
}
</style>
